// Full-screen first-touch interstitial. Shows a zen-ish quote about
// fucking up, with a single Continue affordance whose click is the user
// gesture that unlocks browser audio for the rest of the session.
//
// Shown once per ~24h session. Dismiss state stored in localStorage so
// the user doesn't see it on every navigation. After dismiss:
//   - music.enable() fires (which starts the player)
//   - Shao Kahn voice fires (the per-page startup taunt)
//   - Music fades in at the same time, ducked to ~12% so the voice
//     cuts through. When voice ends, music un-ducks back to ~55%.

import { useEffect, useState } from "react";
import { useMusic } from "../lib/music";

const STORAGE_KEY = "interstitialShownAt";
const SESSION_HOURS = 24;

// Quotes live in src/client/lib/quotes.ts so the slideshow can pull from
// the same pool for its every-3-slides interstitial card.
import { pickRandomQuote } from "../lib/quotes";

function pickQuote() { return pickRandomQuote(); }

function isStillFresh(): boolean {
  try {
    const t = localStorage.getItem(STORAGE_KEY);
    if (!t) return false;
    const n = Number(t);
    if (!Number.isFinite(n)) return false;
    return Date.now() - n < SESSION_HOURS * 60 * 60 * 1000;
  } catch {
    return false;
  }
}
function markShown(): void {
  try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
}

export function MusicInterstitial({ onDismiss }: { onDismiss: (isNewSession: boolean) => void }) {
  const music = useMusic();
  const [show, setShow] = useState(() => !isStillFresh() && !music.enabled);
  const [fading, setFading] = useState(false);
  const [quote] = useState(() => pickQuote());

  // If music is already enabled (e.g. user toggled it via header), no point
  // showing the interstitial. Same if the session timestamp is fresh.
  // We pass isNewSession=false so the parent doesn't fire Shao Kahn —
  // the user already heard him on the original visit, no need to repeat
  // every time they reload a deep link.
  useEffect(() => {
    if (music.enabled || isStillFresh()) {
      setShow(false);
      onDismiss(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = () => {
    if (fading) return;
    setFading(true);
    music.enable();
    markShown();
    // Wait the fade-out duration before unmounting so the screen doesn't snap.
    // isNewSession=true → parent fires the Shao Kahn taunt to greet a
    // genuinely-fresh session.
    setTimeout(() => {
      setShow(false);
      onDismiss(true);
    }, 700);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center"
      style={{
        background:
          "radial-gradient(ellipse at center, rgba(22,24,26,1) 0%, rgba(11,12,13,1) 70%, #000 100%)",
        opacity: fading ? 0 : 1,
        transition: "opacity 700ms ease-out",
        pointerEvents: fading ? "none" : "auto",
      }}
    >
      <div className="container flex flex-col items-center text-center" style={{ maxWidth: 760 }}>
        {/* Quote — generous breathing room, big italic Fraunces */}
        <blockquote
          className="text-[color:var(--color-text)]"
          style={{
            fontFamily: "var(--font-serif)",
            fontStyle: "italic",
            fontSize: "clamp(28px, 4.6vw, 56px)",
            lineHeight: 1.18,
            letterSpacing: "-0.015em",
            fontVariationSettings: '"opsz" 144, "SOFT" 100',
            fontWeight: 500,
            // Subtle staggered reveal — the quote breathes in
            animation: "interstitial-quote-in 1400ms ease-out 200ms both",
          }}
        >
          &ldquo;{quote.text}&rdquo;
        </blockquote>
        {quote.attrib && (
          <div
            className="mt-6 text-[color:var(--color-text-dim)]"
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontSize: "clamp(14px, 1.1vw, 17px)",
              letterSpacing: "0.02em",
              animation: "interstitial-attrib-in 1400ms ease-out 700ms both",
            }}
          >
            — {quote.attrib}
          </div>
        )}

        {/* Continue button — soft glow, subtle pulse */}
        <button
          onClick={handleContinue}
          className="mt-16 group inline-flex items-center gap-3 rounded-full px-8 py-3.5 transition-all duration-300 hover:-translate-y-0.5"
          style={{
            background: "rgba(251, 113, 133, 0.08)",
            border: "1px solid rgba(251, 113, 133, 0.35)",
            color: "var(--color-rose-hi)",
            fontFamily: "var(--font-body)",
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: "0.02em",
            animation: "interstitial-button-in 1200ms ease-out 1500ms both",
          }}
        >
          <span>Continue</span>
          <span style={{ transform: "translateY(-1px)", transition: "transform 300ms" }} className="group-hover:translate-x-0.5">
            →
          </span>
        </button>
        <div
          className="mt-3 text-[color:var(--color-text-dim)]"
          style={{
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            opacity: 0.7,
            animation: "interstitial-button-in 1200ms ease-out 1900ms both",
          }}
        >
          Sound on
        </div>
      </div>
      <style>{`
        @keyframes interstitial-quote-in {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes interstitial-attrib-in {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes interstitial-button-in {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
